import clsx from 'clsx';
import { PropsWithChildren, useRef } from 'react';

import Backdrop from '../Backdrop';
import CloseIcon from '../Icon/CloseIcon';
import Portal from '../Portal';

import styles from './Dialog.module.css';
import { BackdropClick, DialogComponentProps } from './types';

const Dialog = ({
  className,
  title,
  open,
  disableBackdropClick,
  children,
  contentClassName,
  showBottomButton,
  dialogHeaderClassName,
  rootClass,
  paperClassName,
  iconClassName,
  onClose,
  dialogBackdropClassName,
  closeIconWidth,
  closeIconHeight,
  closeIconClassName,
}: PropsWithChildren<DialogComponentProps>) => {
  const backdropClick: BackdropClick = useRef() as BackdropClick;

  const handleMouseDown = (event: React.MouseEvent<HTMLElement>): void => {
    backdropClick.current = event.target === event.currentTarget;
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLElement>): void => {
    // Ignore the events not coming from the "backdrop" (if backdropClick.current === false).
    // if we click inside dialog the modal shoudl remain opened
    if (!backdropClick.current || disableBackdropClick) {
      return;
    }
    backdropClick.current = null;

    if (onClose) {
      onClose(event, 'backdropClick');
    }
  };

  return (
    <>
      {children && open && (
        <Portal selector="#portal">
          {' '}
          <div
            className={clsx(styles.DialogRoot, styles.DialogRootAnimation, rootClass)}
            role="presentation"
            onClick={handleBackdropClick}
          >
            <Backdrop className={dialogBackdropClassName} />
            <div
              className={clsx(styles.DialogContainer, { [styles.DialogContainerAnimation]: open }, className)}
              role="none presentation"
              tabIndex={-1}
              onMouseDown={handleMouseDown}
            >
              <div className={clsx(styles.DialogPaper, styles.DialogPaperShadow, paperClassName)}>
                <span className={clsx(styles.DialogHeader, dialogHeaderClassName)}>
                  {title && <span className={styles.DialogHeaderTitle}>{title}</span>}
                  <span className={clsx(styles.DialogHeaderIconContainer, iconClassName)}>
                    <CloseIcon
                      id="dialog-close-icon"
                      className={clsx(styles.DialogCloseIcon, closeIconClassName)}
                      width={closeIconWidth}
                      height={closeIconHeight}
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        if (onClose) {
                          onClose(event, 'stateChange');
                        }
                      }}
                    />
                  </span>
                </span>
                <div className={clsx(styles.DialogContentContainer, contentClassName)}>{children}</div>
              </div>
            </div>
            {showBottomButton && (
              <span
                data-testid={'bottom-close'}
                className={styles.bottomClose}
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  if (onClose) {
                    onClose(event, 'stateChange');
                  }
                }}
              >
                Close
              </span>
            )}
          </div>
        </Portal>
      )}
    </>
  );
};

export default Dialog;
